// src/components/ScreenThree/index.tsx
import React from "react";
import styled from "styled-components";
import {
    SFText18W400 as Text18W400, SFText18W400Div,
    SText14W400 as Text14W400,
    SText40W700 as Text40W700,
    Text24W700
} from "../Typography";
import TradingViewChart from "./TradingViewChart";
import TradingViewSymbolInfo from "./TradingViewSymbolInfo";
import {useSingleton} from "cms/factory";
import {AsyncReveal} from "../AsyncReveal";

/* ===================== TYPES ===================== */
type StatCard = {
    id: string;
    value: string;
    label: string;
    color?: string | null;
    order: number;
};

type IndexItem = {
    id: string;
    text: string;
    order: number;
};

type ScreenThreeData = {
    id: string;
    title?: string | null;
    description?: string | null;
    footnote?: string | null;
    statCards: StatCard[];
    indexes: IndexItem[];
};

/* ===================== GQL SELECTION ===================== */
const SELECTION = `
  id
  title
  description
  footnote
  statCards(orderBy: { order: asc }) { id value label color order }
  indexes(orderBy: { order: asc }) { id text order }
`;

/* ===================== UTILS ===================== */
function chunkInto<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

/* ===================== HOOKS ===================== */
// function useIsMobile(maxWidth = 768) {
//     const [isMobile, setIsMobile] = React.useState(
//         typeof window !== "undefined" ? window.innerWidth <= maxWidth : false
//     );
//
//     React.useEffect(() => {
//         const media = window.matchMedia(`(max-width: ${maxWidth}px)`);
//         const listener = (e: MediaQueryListEvent) => setIsMobile(e.matches);
//         media.addEventListener("change", listener);
//         return () => media.removeEventListener("change", listener);
//     }, [maxWidth]);
//
//     return isMobile;
// }

/* ===================== STYLES ===================== */
const ScreenSecondBlock = styled.div`
    width: 100%;
    max-width: 1380px;
    padding: 64px 10px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    @media (max-width: 768px) {
        padding: 48px 10px;
    }
`;

const ContentCard = styled.div`
    width: 90%;
    max-width: 1200px;
    background: #fff;
    border-radius: 16px;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 48px;

    @media (max-width: 768px) {
        flex-direction: column-reverse;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 32px;
    }
`;

const TitleBlock = styled.div`
    max-width: 720px;
    display: flex;
    flex-direction: column;
`;

const ChartAndStats = styled.div`
    display: flex;
    gap: 32px;
    align-items: stretch;

    @media (max-width: 768px) {
        flex-direction: column;
        display: block;
    }
`;

const ChartWrapper = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    @media (max-width: 768px) {
        width: 100%;
        margin-bottom: 32px;
    }
`;

const ChartWrapperIn = styled.div`
    flex: 1;
    border: 1px solid #F5F8FB;
    border-radius: 8px;
    @media (max-width: 768px) {
        display: grid;
        width: 100%;
        min-height: 436px;
        height: 436px !important;
    }
`;

const ChartDesc = styled.div`
    margin-top: 48px;
    @media (max-width: 768px) {
        margin-top: 16px;
    }
`;

const StatsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: stretch;

    @media (max-width: 768px) {
        width: 100%;
        gap: 16px;
    }
`;

const InfoWrapper = styled.div`
    border: 1px solid #F5F8FB;
    border-radius: 8px;
    gap: 24px;
    min-width: 450px;
    @media (max-width: 1200px) {
        min-width: 400px;
    }
    @media (max-width: 1000px) {
        min-width: 350px;
    }
    @media (max-width: 768px) {
        min-width: auto;
        width: 100%;
    }
`;

const StatCardRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: stretch;
    display: none;

    @media (max-width: 768px) {
        width: 100%;
        flex-direction: column;
    }
`;

const StatCardBox = styled.div`
    background: #F5F8FB;
    border-radius: 12px;
    padding: 33px 32px;
    text-align: left;
    display: flex;
    flex-direction: column;
    width: min-content;
    min-height: 188px;

    @media (max-width: 768px) {
        min-height: auto;
        width: 100%;
    }
`;

const IndexesBlock = styled.div`
    border-radius: 12px;

    ul {
        margin-top: 12px;
        padding-left: 20px;
        list-style-type: disc;
        list-style-position: outside;
    }

    li {
        margin: 6px 0;
    }

    @media (max-width: 768px) {
        margin-top: 0;
    }
`;

/* ===================== COMPONENT ===================== */
function ScreenThree() {
    const {data: s3, loading, error} = useSingleton<ScreenThreeData>(
        "ScreenThreeSection",
        SELECTION
    );

    const cardGroups = chunkInto<StatCard>(s3?.statCards ?? [], 2);
    const indexes: IndexItem[] = s3?.indexes ?? [];

    //const isMobile = useIsMobile();

    const ChartFooter = (
        <SFText18W400Div style={{color: "#ACACAD"}}>
            <ChartDesc>
                For more information, visit:{" "}
                <a
                    href="https://ir.freedomholdingcorp.com/"
                    style={{color: "#16A34A"}}
                >
                    {"Freedom Holding Corp Investor Relations"}
                </a>
            </ChartDesc>
        </SFText18W400Div>
    );

    return (
        <ScreenSecondBlock id="business_areas">
            <ContentCard>
                <AsyncReveal loading={loading} error={error} stagger={0.08} from={12}>
                    {/* Заголовок + описание */}
                    <HeaderRow>
                        <TitleBlock>
                            {s3?.title ? (
                                <div style={{marginBottom: 24}}>
                                    <Text40W700>{s3.title}</Text40W700>
                                </div>
                            ) : null}

                            {s3?.description ? (
                                <Text18W400
                                    style={{textAlign: 'left'}}
                                    dangerouslySetInnerHTML={{
                                        __html: s3.description.replace(/\n/g, "<br />"),
                                    }}
                                />
                            ) : null}
                        </TitleBlock>
                    </HeaderRow>

                    <ChartAndStats>
                        <ChartWrapper>
                            <ChartWrapperIn>
                                <TradingViewChart/>
                            </ChartWrapperIn>
                        </ChartWrapper>
                        <StatsWrapper>
                            <InfoWrapper>
                                <TradingViewSymbolInfo/>
                            </InfoWrapper>
                            {/* Карточки — группами по 2 */}
                            {cardGroups.map((row: StatCard[], ri: number) => (
                                <StatCardRow key={`row-${ri}`}>
                                    {row.map((card: StatCard) => (
                                        <StatCardBox key={card.id}>
                                            {card.value ? (
                                                <Text24W700
                                                    style={{
                                                        color: card.color || "#16a34a",
                                                        whiteSpace: "nowrap",
                                                        marginBottom: 24,
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    {card.value}
                                                </Text24W700>
                                            ) : null}
                                            {card.label ? (
                                                <Text18W400 style={{textAlign: "left"}}>
                                                    {card.label}
                                                </Text18W400>
                                            ) : null}
                                        </StatCardBox>
                                    ))}
                                </StatCardRow>
                            ))}

                            {/* Индексы */}
                            {indexes.length > 0 ? (
                                <IndexesBlock>
                                    <Text24W700 style={{textAlign: "left"}}>
                                        FRHC is a part of indexes:
                                    </Text24W700>
                                    <ul>
                                        {indexes.map((it: IndexItem) => (
                                            <li key={it.id}>
                                                <Text18W400 style={{textAlign: "left"}}>
                                                    {it.text}
                                                </Text18W400>
                                            </li>
                                        ))}
                                    </ul>
                                </IndexesBlock>
                            ) : null}

                            {/* Сноска */}
                            {s3?.footnote ? (
                                <Text14W400 style={{textAlign: "left"}}>
                                    {s3.footnote}
                                </Text14W400>
                            ) : null}
                        </StatsWrapper>
                    </ChartAndStats>

                    {/* На десктопе футер здесь */}
                    {ChartFooter}
                </AsyncReveal>
            </ContentCard>
        </ScreenSecondBlock>
    );
}

export default ScreenThree;
